import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { problemMessage } from "@/lib/account";
import type { components } from "@breenova/api-client";

type Schemas = components["schemas"];

export type AdminUser = Schemas["AdminUserResponse"];
export type ApprovalStatus = Schemas["UserApprovalStatus"];

export const adminKeys = {
  all: ["admin"] as const,
  users: (status: ApprovalStatus | "All") => ["admin", "users", status] as const,
};

/**
 * The accounts on this instance.
 *
 * Kept fresh rather than cached for the usual minute: this is the screen somebody sits on
 * while a breeder in the next room registers, and a list that needs a manual reload to
 * show them is a list that gets reloaded manually forever.
 */
export function useAdminUsers(status: ApprovalStatus | "All") {
  return useQuery({
    queryKey: adminKeys.users(status),
    queryFn: async () => {
      const { data, error } = await api.GET("/api/v1/admin/users", {
        params: { query: status === "All" ? {} : { Status: status } },
      });

      if (error || !data) {
        throw new Error(problemMessage(error, "Could not load the accounts."));
      }

      return data;
    },
    staleTime: 0,
    refetchInterval: 30_000,
  });
}

export function useApproveUser() {
  return useAccountDecision(
    (userId) =>
      api.POST("/api/v1/admin/users/{id}/approve", { params: { path: { id: userId } } }),
    "Could not approve that account.",
  );
}

export function useRejectUser() {
  return useAccountDecision(
    (userId, note) =>
      api.POST("/api/v1/admin/users/{id}/reject", {
        params: { path: { id: userId } },
        body: { userId, note: note ?? null },
      }),
    "Could not turn that account away.",
  );
}

export function useSetUserRoles() {
  return useAccountDecision(
    (userId, _note, roles) =>
      api.PUT("/api/v1/admin/users/{id}/roles", {
        params: { path: { id: userId } },
        body: { userId, roles: roles ?? [] },
      }),
    "Could not change that account's roles.",
  );
}

/**
 * The shape every decision shares: send it, take the account the server answers with, and
 * throw every cached list away.
 *
 * Invalidated wholesale rather than patched into place, because one decision moves an
 * account between two lists and changes the count on a third. Reconciling that by hand is
 * how a screen ends up showing an approved breeder in the waiting queue.
 */
function useAccountDecision(
  send: (
    userId: string,
    note?: string | null,
    roles?: string[],
  ) => Promise<{ data?: AdminUser; error?: unknown }>,
  fallbackMessage: string,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      userId: string;
      note?: string | null;
      roles?: string[];
    }): Promise<AdminUser> => {
      const { data, error } = await send(input.userId, input.note, input.roles);

      if (error || !data) {
        throw new Error(problemMessage(error, fallbackMessage));
      }

      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: adminKeys.all });
    },
  });
}
